(() => {
  const FREE_SHIPPING_THRESHOLD = 15;

  const toInt = (value, fallback = 0) => {
    const parsed = parseInt(value, 10);
    return Number.isFinite(parsed) ? parsed : fallback;
  };

  const moneyFormatter = (currency) => {
    try {
      return new Intl.NumberFormat('es-ES', { style: 'currency', currency });
    } catch (error) {
      return new Intl.NumberFormat('es-ES', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    }
  };

  const shippingText = (pairs, compact = false) => {
    const safePairs = Math.max(0, toInt(pairs, 0));
    if (safePairs >= FREE_SHIPPING_THRESHOLD) {
      return compact ? 'Portes gratis' : `✓ ${safePairs} pares · PORTES GRATIS`;
    }
    const remaining = Math.max(0, FREE_SHIPPING_THRESHOLD - safePairs);
    return compact
      ? `Faltan ${remaining} para portes gratis`
      : `${safePairs} pares · faltan ${remaining} para portes gratis`;
  };

  const updateShippingUI = (pairs) => {
    const safePairs = Math.max(0, toInt(pairs, 0));

    document.querySelectorAll('[data-clayan-b2b-cart-count]').forEach((badge) => {
      badge.textContent = String(safePairs);
    });

    document.querySelectorAll('[data-clayan-b2b-shipping-status]').forEach((el) => {
      const compact = el.closest('.clayan-b2b-summary-fab') !== null;
      el.dataset.currentPairs = String(safePairs);
      el.textContent = shippingText(safePairs, compact);
      el.classList.toggle('is-free', safePairs >= FREE_SHIPPING_THRESHOLD);
    });

    document.querySelectorAll('clayan-b2b-order').forEach((order) => {
      order.dataset.cartPairs = String(safePairs);
      if (typeof order.updateSummary === 'function') order.updateSummary();
    });
  };

  const refreshCartMeta = async () => {
    try {
      const root = window.Shopify?.routes?.root || '/';
      const response = await fetch(`${root}cart.js`, {
        headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      });
      if (!response.ok) return;
      const cart = await response.json();
      updateShippingUI(cart.item_count || 0);
    } catch (error) {
      // Non-critical UI enhancement. Shopify cart remains functional if this request fails.
    }
  };

  class ClayanB2BOrder extends HTMLElement {
    connectedCallback() {
      if (this.dataset.ready === 'true') return;
      this.dataset.ready = 'true';

      this.inputs = Array.from(this.querySelectorAll('[data-b2b-qty]'));
      this.totalPairs = this.querySelector('[data-b2b-total-pairs]');
      this.totalAmount = this.querySelector('[data-b2b-total-amount]');
      this.shippingPreview = this.querySelector('[data-b2b-shipping-preview]');
      this.status = this.querySelector('[data-b2b-status]');
      this.addButton = this.querySelector('[data-b2b-add]');
      this.currency = this.dataset.currency || 'EUR';
      this.formatMoney = moneyFormatter(this.currency);

      this.addEventListener('click', this.onClick.bind(this));
      this.addEventListener('input', this.onInput.bind(this));
      this.addButton?.addEventListener('click', this.addToCart.bind(this));
      this.updateSummary();
    }

    normalizeInput(input) {
      let value = Math.max(0, toInt(input.value, 0));
      const min = Math.max(1, toInt(input.dataset.min, 1));
      const increment = Math.max(1, toInt(input.dataset.increment, 1));
      const maxRaw = toInt(input.dataset.max, 0);
      const max = maxRaw > 0 ? maxRaw : null;

      if (value > 0 && value < min) value = min;
      if (value > 0) {
        const offset = Math.max(0, value - min);
        value = min + Math.round(offset / increment) * increment;
      }
      if (max !== null) value = Math.min(value, max);

      input.value = String(value);
      return value;
    }

    onInput(event) {
      const input = event.target.closest('[data-b2b-qty]');
      if (!input) return;
      this.normalizeInput(input);
      this.updateSummary();
    }

    onClick(event) {
      const stepButton = event.target.closest('[data-b2b-step]');
      if (stepButton) {
        const wrapper = stepButton.closest('[data-b2b-variant]');
        const input = wrapper?.querySelector('[data-b2b-qty]');
        if (!input) return;

        const direction = stepButton.dataset.b2bStep;
        const current = toInt(input.value, 0);
        const min = Math.max(1, toInt(input.dataset.min, 1));
        const increment = Math.max(1, toInt(input.dataset.increment, 1));
        const maxRaw = toInt(input.dataset.max, 0);
        const max = maxRaw > 0 ? maxRaw : null;

        let next;
        if (direction === 'plus') {
          next = current === 0 ? min : current + increment;
          if (max !== null) next = Math.min(next, max);
        } else {
          next = current <= min ? 0 : Math.max(0, current - increment);
        }

        input.value = String(next);
        this.updateSummary();
        return;
      }

      const preset = event.target.closest('[data-b2b-preset]');
      if (!preset) return;
      if (preset.dataset.b2bPreset === 'one') {
        this.inputs.forEach((input) => {
          if (!input.disabled) input.value = String(Math.max(1, toInt(input.dataset.min, 1)));
        });
      } else if (preset.dataset.b2bPreset === 'clear') {
        this.inputs.forEach((input) => { input.value = '0'; });
      }
      this.updateSummary();
    }

    updateSummary() {
      let pairs = 0;
      let cents = 0;

      this.inputs.forEach((input) => {
        const quantity = Math.max(0, toInt(input.value, 0));
        const price = Math.max(0, toInt(input.dataset.price, 0));
        pairs += quantity;
        cents += quantity * price;
      });

      if (this.totalPairs) this.totalPairs.textContent = String(pairs);
      if (this.totalAmount) this.totalAmount.textContent = this.formatMoney.format(cents / 100);
      if (this.addButton) this.addButton.disabled = pairs === 0;

      if (this.shippingPreview) {
        const currentCartPairs = Math.max(0, toInt(this.dataset.cartPairs, 0));
        const projected = currentCartPairs + pairs;
        if (projected >= FREE_SHIPPING_THRESHOLD) {
          this.shippingPreview.textContent = `✓ Con esta selección llegarás a ${projected} pares: PORTES GRATIS.`;
          this.shippingPreview.classList.add('is-free');
        } else {
          const remaining = FREE_SHIPPING_THRESHOLD - projected;
          this.shippingPreview.textContent = `Con esta selección tendrás ${projected} pares. Te faltarán ${remaining} para portes gratis.`;
          this.shippingPreview.classList.remove('is-free');
        }
      }

      this.clearStatus();
    }

    clearStatus() {
      if (!this.status) return;
      this.status.textContent = '';
      this.status.classList.remove('clayan-b2b-order__status--error');
    }

    setStatus(message, error = false) {
      if (!this.status) return;
      this.status.textContent = message;
      this.status.classList.toggle('clayan-b2b-order__status--error', error);
    }

    async addToCart() {
      const items = this.inputs
        .map((input) => ({
          id: toInt(input.dataset.variantId, 0),
          quantity: Math.max(0, toInt(input.value, 0)),
        }))
        .filter((item) => item.id && item.quantity > 0);

      if (!items.length || !this.addButton) return;

      const originalText = this.addButton.textContent;
      this.addButton.disabled = true;
      this.addButton.setAttribute('aria-busy', 'true');
      this.addButton.textContent = 'Añadiendo…';
      this.setStatus('');

      const cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
      const payload = { items };

      if (cart && typeof cart.getSectionsToRender === 'function') {
        payload.sections = cart.getSectionsToRender().map((section) => section.id);
        payload.sections_url = window.location.pathname;
        if (typeof cart.setActiveElement === 'function') cart.setActiveElement(this.addButton);
      }

      try {
        const response = await fetch(`${this.dataset.cartAddUrl}`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json',
            'X-Requested-With': 'XMLHttpRequest',
          },
          body: JSON.stringify(payload),
        });

        const data = await response.json();
        if (!response.ok || data.status) {
          throw new Error(data.description || data.message || 'No se pudo añadir el pedido al carrito.');
        }

        if (cart && typeof cart.renderContents === 'function' && data.sections) {
          if (cart.classList?.contains('is-empty')) cart.classList.remove('is-empty');
          cart.renderContents(data);
        }

        if (window.publish && window.PUB_SUB_EVENTS?.cartUpdate) {
          window.publish(window.PUB_SUB_EVENTS.cartUpdate, {
            source: 'clayan-b2b-order',
            cartData: data,
          });
        }

        const addedQuantity = items.reduce((sum, item) => sum + item.quantity, 0);

        items.forEach((item) => {
          const input = this.inputs.find((candidate) => toInt(candidate.dataset.variantId, 0) === item.id);
          if (!input) return;
          if (input.dataset.stockTracked === 'true') {
            const oldMax = toInt(input.dataset.max, 0);
            const nextMax = Math.max(0, oldMax - item.quantity);
            input.dataset.max = String(nextMax);
            input.max = String(nextMax);
            const card = input.closest('[data-b2b-variant]');
            const stock = card?.querySelector('[data-b2b-stock] strong');
            if (stock) stock.textContent = String(nextMax);
            if (nextMax <= 0) {
              input.disabled = true;
              card?.classList.add('clayan-b2b-variant--soldout');
            }
          }
          input.value = '0';
        });

        this.setStatus(`${addedQuantity} pares añadidos al carrito.`);
        await refreshCartMeta();
        this.updateSummary();

        if (!cart) window.location.assign(this.dataset.cartUrl);
      } catch (error) {
        this.setStatus(error.message || 'No se pudo añadir el pedido al carrito.', true);
      } finally {
        this.addButton.removeAttribute('aria-busy');
        this.addButton.textContent = originalText;
        this.addButton.disabled = this.inputs.every((input) => toInt(input.value, 0) === 0);
      }
    }
  }

  if (!customElements.get('clayan-b2b-order')) {
    customElements.define('clayan-b2b-order', ClayanB2BOrder);
  }

  const initialShippingElement = document.querySelector('[data-clayan-b2b-shipping-status]');
  if (initialShippingElement) {
    updateShippingUI(toInt(initialShippingElement.dataset.currentPairs, 0));
  }

  if (typeof window.subscribe === 'function' && window.PUB_SUB_EVENTS?.cartUpdate) {
    window.subscribe(window.PUB_SUB_EVENTS.cartUpdate, (event) => {
      const count = event?.cartData?.item_count;
      if (Number.isFinite(Number(count))) updateShippingUI(Number(count));
      else refreshCartMeta();
    });
  } else if (typeof subscribe === 'function' && window.PUB_SUB_EVENTS?.cartUpdate) {
    subscribe(window.PUB_SUB_EVENTS.cartUpdate, (event) => {
      const count = event?.cartData?.item_count;
      if (Number.isFinite(Number(count))) updateShippingUI(Number(count));
      else refreshCartMeta();
    });
  }


  const bindContinueShopping = () => {
    document.addEventListener('click', (event) => {
      const button = event.target.closest('[data-clayan-b2b-continue-shopping]');
      if (!button) return;
      event.preventDefault();
      const drawer = button.closest('cart-drawer') || document.querySelector('cart-drawer');
      if (drawer && typeof drawer.close === 'function') {
        drawer.close();
        return;
      }
      const closeButton = drawer?.querySelector('.drawer__close');
      if (closeButton) closeButton.click();
    });
  };

  bindContinueShopping();

  document.addEventListener('cart:updated', refreshCartMeta);
  document.addEventListener('DOMContentLoaded', () => {
    const el = document.querySelector('[data-clayan-b2b-shipping-status]');
    if (el) updateShippingUI(toInt(el.dataset.currentPairs, 0));
  });


  class ClayanB2BFinalOrder extends HTMLElement {
    connectedCallback() {
      if (this.dataset.serverReady === 'true') return;
      this.dataset.serverReady = 'true';

      this.endpoint = (this.dataset.endpoint || '/apps/clayan-b2b').replace(/\/$/, '');
      this.currency = this.dataset.currency || 'EUR';
      this.formatMoney = moneyFormatter(this.currency);
      this.subtotalCents = Math.max(0, toInt(this.dataset.cartSubtotalCents, 0));
      this.pairs = Math.max(0, toInt(this.dataset.cartPairs, 0));
      this.hasRE = this.dataset.hasRe === 'true';
      this.serviceReady = false;
      this.context = null;
      this.preview = null;
      this.habitualCents = 0;
      this.habitualPercent = 0;
      this.originalCents = this.subtotalCents;
      this.merchandiseCents = this.subtotalCents;
      this.submitButton = this.querySelector('[data-b2b-submit-order]');
      this.confirm = this.querySelector('[data-b2b-confirm]');
      this.message = this.querySelector('[data-b2b-order-message]');
      this.serviceStatus = this.querySelector('[data-b2b-service-status]');
      this.success = this.querySelector('[data-b2b-success]');

      const configNode = this.querySelector('[data-b2b-order-config]');
      try { this.config = JSON.parse(configNode?.textContent || '{}'); }
      catch (error) { this.config = {}; }
      this.habitualPercent = Number(this.config?.habitual_discount_percent || 0) || 0;

      try {
        this.requestId = sessionStorage.getItem('clayanB2BOrderRequestId');
        if (!this.requestId) {
          this.requestId = window.crypto?.randomUUID?.() || `clayan-${Date.now()}-${Math.random().toString(36).slice(2)}`;
          sessionStorage.setItem('clayanB2BOrderRequestId', this.requestId);
        }
      } catch (error) {
        this.requestId = window.crypto?.randomUUID?.() || `clayan-${Date.now()}-${Math.random().toString(36).slice(2)}`;
      }

      this.addEventListener('change', this.onChange.bind(this));
      this.submitButton?.addEventListener('click', this.submitOrder.bind(this));
      this.updateLocalTotals();
      this.loadContextAndPreview();
    }

    currentPaymentMode() {
      return this.querySelector('[data-b2b-payment-option]:checked')?.value || 'habitual';
    }

    setServiceStatus(text, state = '') {
      if (!this.serviceStatus) return;
      this.serviceStatus.textContent = text;
      this.serviceStatus.dataset.state = state;
    }

    setMessage(text = '', error = false) {
      if (!this.message) return;
      this.message.textContent = text;
      this.message.classList.toggle('is-error', error);
      this.message.classList.toggle('is-success', Boolean(text) && !error);
    }

    money(cents) {
      return this.formatMoney.format((Number(cents) || 0) / 100);
    }

    setMoney(selector, cents, prefix = '') {
      const el = this.querySelector(selector);
      if (el) el.textContent = `${prefix}${this.money(cents)}`;
    }

    updateLocalTotals() {
      const early = this.currentPaymentMode() === 'pronto_pago';
      const merchandiseCents = Math.max(0, Number(this.merchandiseCents ?? this.subtotalCents) || 0);
      const earlyCents = early ? Math.round(merchandiseCents * 0.03) : 0;
      const shippingCents = this.pairs >= FREE_SHIPPING_THRESHOLD ? 0 : 1000;
      const baseCents = Math.max(0, merchandiseCents - earlyCents + shippingCents);
      const ivaCents = Math.round(baseCents * 0.21);
      const reCents = this.hasRE ? Math.round(baseCents * 0.052) : 0;
      const totalCents = baseCents + ivaCents + reCents;

      this.calculation = { earlyCents, shippingCents, baseCents, ivaCents, reCents, totalCents };

      const earlyRow = this.querySelector('[data-b2b-early-row]');
      if (earlyRow) earlyRow.hidden = !early;
      this.setMoney('[data-b2b-merchandise]', merchandiseCents);
      this.setMoney('[data-b2b-early-discount]', earlyCents, '−');
      const shipping = this.querySelector('[data-b2b-shipping-amount]');
      if (shipping) shipping.textContent = shippingCents === 0 ? 'GRATIS' : this.money(shippingCents);
      this.setMoney('[data-b2b-tax-base]', baseCents);
      this.setMoney('[data-b2b-iva]', ivaCents);
      const reRow = this.querySelector('[data-b2b-re-row]');
      if (reRow) reRow.hidden = !this.hasRE;
      this.setMoney('[data-b2b-re]', reCents);
      this.setMoney('[data-b2b-grand-total]', totalCents);

      this.querySelectorAll('.clayan-b2b-payment-option').forEach((option) => {
        option.classList.toggle('is-selected', option.querySelector('input')?.checked === true);
      });
    }

    onChange(event) {
      if (event.target.matches('[data-b2b-payment-option]')) {
        this.updateLocalTotals();
        if (this.serviceReady) this.refreshPreview().catch(() => {});
      }
    }

    fieldValue(name) {
      return this.querySelector(`[data-b2b-shipping-field="${name}"]`)?.value?.trim() || '';
    }

    addressLine() {
      const parts = [
        this.fieldValue('company'),
        this.fieldValue('address1'),
        this.fieldValue('address2'),
        [this.fieldValue('zip'), this.fieldValue('city')].filter(Boolean).join(' '),
        this.fieldValue('province'),
        this.fieldValue('country'),
      ].filter(Boolean);
      return parts.join('\n');
    }

    text(selector, fallback = '—') {
      const value = this.querySelector(selector)?.textContent?.trim();
      return value || fallback;
    }

    splitModelDescription(title = '') {
      const clean = String(title || '').replace(/\s+/g, ' ').trim();
      const numeric = clean.match(/\b(\d{1,5}[A-Za-z]?)\b/);
      if (numeric) {
        const model = numeric[1];
        const description = `${clean.slice(0, numeric.index)} ${clean.slice((numeric.index || 0) + numeric[0].length)}`
          .replace(/\s+/g, ' ').trim();
        return { model, description: description || clean };
      }
      const parts = clean.split(' ').filter(Boolean);
      const model = parts.shift() || '—';
      return { model, description: parts.join(' ') || clean || '—' };
    }

    variantSize(variantTitle = '') {
      const match = String(variantTitle || '').match(/(?:^|\D)(3[5-9]|4[0-9])(?:\D|$)/);
      return match ? Number(match[1]) : null;
    }

    groupedOrderRows() {
      const items = Array.isArray(this.config?.items) ? this.config.items : [];
      const groups = new Map();

      items.forEach((item) => {
        const parsed = this.splitModelDescription(item.title || 'Artículo');
        const key = `${parsed.model}|||${parsed.description}`;
        if (!groups.has(key)) {
          groups.set(key, {
            model: parsed.model,
            description: parsed.description,
            sizes: Object.fromEntries(Array.from({ length: 15 }, (_, i) => [35 + i, 0])),
            other: 0,
            total: 0,
          });
        }
        const row = groups.get(key);
        const qty = Math.max(0, Number(item.quantity) || 0);
        const size = this.variantSize(item.variant_title);
        if (size && size >= 35 && size <= 49) row.sizes[size] += qty;
        else row.other += qty;
        row.total += qty;
      });

      return Array.from(groups.values());
    }

    matrixText() {
      const rows = this.groupedOrderRows();
      const sizes = Array.from({ length: 15 }, (_, i) => 35 + i);
      const lines = [
        `MODELO | DESCRIPCIÓN | ${sizes.join(' | ')} | TOTAL`,
        '-'.repeat(150),
      ];
      rows.forEach((row) => {
        const qtys = sizes.map((size) => row.sizes[size] || '-');
        lines.push(`${row.model} | ${row.description} | ${qtys.join(' | ')} | ${row.total}`);
        if (row.other > 0) lines.push(`  (Otras variantes/tallas: ${row.other} par(es))`);
      });
      return lines.join('\n');
    }

    orderSnapshot() {
      const mode = this.currentPaymentMode();
      return {
        savedAt: new Date().toISOString(),
        dateText: new Date().toLocaleString('es-ES'),
        currency: this.currency,
        company: this.context?.company?.name || this.config?.company_name || this.text('[data-b2b-company-name]'),
        contact: this.context?.contacto_empresa || this.config?.contact_name || this.config?.customer_name || '—',
        email: this.context?.customer?.email || this.config?.customer_email || '—',
        taxId: this.context?.cif_dni || this.context?.tax_registration_id || this.config?.tax_id || this.text('[data-b2b-tax-id]'),
        agent: this.context?.agente_comercial || this.config?.agent_name || '—',
        phone: this.fieldValue('phone') || '—',
        shipping: this.addressLine() || '—',
        payment: mode === 'pronto_pago' ? 'Transferencia · pronto pago −3 %' : 'Mis condiciones habituales',
        reference: this.querySelector('[data-b2b-po-number]')?.value.trim() || '—',
        note: this.querySelector('[data-b2b-order-note]')?.value.trim() || '—',
        rows: this.groupedOrderRows(),
        totalPairs: this.pairs,
        totals: {
          original: this.text('[data-b2b-original-merchandise]', this.money(this.originalCents ?? this.subtotalCents)),
          habitual: (this.habitualCents || 0) > 0 ? this.text('[data-b2b-habitual-discount]', '') : '',
          habitualLabel: (this.habitualCents || 0) > 0 ? this.text('[data-b2b-habitual-label]', 'Descuento habitual') : '',
          hasHabitual: (this.habitualCents || 0) > 0,
          merchandise: this.money(this.merchandiseCents ?? this.subtotalCents),
          early: mode === 'pronto_pago' ? `−${this.money(this.calculation?.earlyCents || 0)}` : 'No aplicado',
          shipping: (this.calculation?.shippingCents || 0) === 0 ? 'GRATIS' : this.money(this.calculation?.shippingCents || 0),
          base: this.money(this.calculation?.baseCents || 0),
          iva: this.money(this.calculation?.ivaCents || 0),
          re: this.hasRE ? this.money(this.calculation?.reCents || 0) : 'No aplica',
          total: this.money(this.calculation?.totalCents || 0),
        },
      };
    }

    saveOrderSnapshot() {
      try {
        sessionStorage.setItem('clayan_b2b_last_order_visual_v1', JSON.stringify(this.orderSnapshot()));
      } catch (error) {}
    }

    emailOrderBlocks() {
      const rows = this.groupedOrderRows();
      if (!rows.length) return 'Sin líneas de pedido';

      const wrapSizes = (sizes, perLine = 7) => {
        const tokens = Object.entries(sizes || {})
          .filter(([, qty]) => Number(qty) > 0)
          .sort(([a], [b]) => Number(a) - Number(b))
          .map(([size, qty]) => `${size}×${qty}`);
        if (!tokens.length) return ['—'];
        const lines = [];
        for (let i = 0; i < tokens.length; i += perLine) {
          lines.push(tokens.slice(i, i + perLine).join(' · '));
        }
        return lines;
      };

      return rows.map((row, index) => {
        const sizeLines = wrapSizes(row.sizes);
        const lines = [
          `${String(index + 1).padStart(2, '0')}. ${row.model} | ${row.description}`,
          `    Tallas: ${sizeLines[0]}`,
        ];
        sizeLines.slice(1).forEach((line) => lines.push(`            ${line}`));
        if (row.other > 0) lines.push(`    Otras variantes: ${row.other} par(es)`);
        lines.push(`    Total modelo: ${row.total} par(es)`);
        return lines.join('\n');
      }).join('\n\n');
    }

    buildEmailBody() {
      const mode = this.currentPaymentMode();
      const paymentText = mode === 'pronto_pago'
        ? 'Transferencia · pronto pago -3 %'
        : 'Mis condiciones habituales';
      const reference = this.querySelector('[data-b2b-po-number]')?.value.trim() || '—';
      const note = this.querySelector('[data-b2b-order-note]')?.value.trim() || '—';
      const now = new Date().toLocaleString('es-ES');
      const company = this.context?.company?.name || this.config?.company_name || this.text('[data-b2b-company-name]');
      const taxId = this.context?.cif_dni || this.context?.tax_registration_id || this.config?.tax_id || this.text('[data-b2b-tax-id]');
      const contactName = this.context?.contacto_empresa || this.config?.contact_name || this.config?.customer_name || '—';
      const agentName = this.context?.agente_comercial || this.config?.agent_name || '—';
      const shippingText = (this.calculation?.shippingCents || 0) === 0
        ? 'GRATIS'
        : this.money(this.calculation?.shippingCents || 0);
      const address = this.addressLine().replace(/\n/g, ' · ') || '—';
      const earlyText = mode === 'pronto_pago'
        ? `-${this.money(this.calculation?.earlyCents || 0)}`
        : 'No aplicado';

      return [
        'PEDIDO B2B · CLAYAN',
        `Fecha: ${now}   |   Total pares: ${this.pairs}`,
        '',
        'CLIENTE',
        `Empresa: ${company}`,
        `NIF / CIF: ${taxId}`,
        `Contacto: ${contactName}   |   Tel.: ${this.fieldValue('phone') || '—'}`,
        `Email: ${this.context?.customer?.email || this.config?.customer_email || '—'}`,
        `Agente comercial: ${agentName}`,
        `Entrega: ${address}`,
        '',
        'CONDICIONES',
        `Pago: ${paymentText}`,
        `Referencia: ${reference}`,
        `Observaciones: ${note}`,
        '',
        'ARTÍCULOS',
        this.emailOrderBlocks(),
        '',
        'RESUMEN ECONÓMICO',
        ...((this.habitualCents || 0) > 0 ? [
          `Subtotal B2B antes de descuento: ${this.money(this.originalCents ?? this.subtotalCents)}`,
          `${this.text('[data-b2b-habitual-label]', 'Descuento habitual')}: ${this.text('[data-b2b-habitual-discount]', '')}`,
        ] : []),
        `Mercancía B2B sin IVA: ${this.money(this.merchandiseCents ?? this.subtotalCents)}`,
        `Pronto pago -3 %: ${earlyText}`,
        `Portes: ${shippingText}`,
        `BASE IMPONIBLE: ${this.money(this.calculation?.baseCents || 0)}`,
        `IVA 21 %: ${this.money(this.calculation?.ivaCents || 0)}`,
        `R.E. 5,2 %: ${this.hasRE ? this.money(this.calculation?.reCents || 0) : 'No aplica'}`,
        `TOTAL PEDIDO: ${this.money(this.calculation?.totalCents || 0)}`,
      ].join('\n');
    }

    proxyUrl(path) {
      const locationId = encodeURIComponent(this.config?.company_location_id || this.dataset.companyLocationId || '');
      return `${this.endpoint}${path}?company_location_id=${locationId}`;
    }

    async fetchJSON(url, options = {}) {
      const response = await fetch(url, {
        credentials: 'same-origin',
        headers: {
          'Accept': 'application/json',
          ...(options.body ? { 'Content-Type': 'application/json' } : {}),
          ...(options.headers || {}),
        },
        ...options,
      });
      let data = null;
      try { data = await response.json(); } catch (error) { data = null; }
      if (!response.ok) {
        const message = data?.detail || data?.error || data?.message || `Error ${response.status}`;
        throw new Error(message);
      }
      return data || {};
    }

    setShippingField(name, value) {
      const input = this.querySelector(`[data-b2b-shipping-field="${name}"]`);
      if (input && value != null) input.value = value;
    }

    applyContext(ctx) {
      this.context = ctx || {};
      if (ctx?.company?.name) {
        const company = this.querySelector('[data-b2b-company-name]');
        if (company) company.textContent = ctx.company.name;
      }
      const tax = this.querySelector('[data-b2b-tax-id]');
      if (tax) tax.textContent = ctx?.cif_dni || ctx?.tax_registration_id || '—';

      const contact = this.querySelector('[data-b2b-contact-name]');
      if (contact) contact.textContent = ctx?.contacto_empresa || ctx?.customer?.name || this.config?.customer_name || '—';

      const customerEmail = this.querySelector('[data-b2b-customer-email]');
      if (customerEmail && ctx?.customer?.email) customerEmail.textContent = ctx.customer.email;

      const agentRow = this.querySelector('[data-b2b-agent-row]');
      const agent = this.querySelector('[data-b2b-agent-name]');
      const agentName = ctx?.agente_comercial || '';
      if (agent) agent.textContent = agentName || '—';
      if (agentRow) agentRow.hidden = !agentName;

      this.habitualPercent = Number(ctx?.descuento_habitual_percent || 0) || 0;

      const payment = this.querySelector('[data-b2b-payment-terms]');
      if (payment) payment.textContent = ctx?.payment_terms?.description || ctx?.payment_terms?.translatedName || ctx?.payment_terms?.name || 'Condiciones habituales autorizadas por Clayan.';

      this.hasRE = Boolean(ctx?.recargo_equivalencia);
      this.dataset.hasRe = this.hasRE ? 'true' : 'false';

      const shipping = ctx?.shipping_address || {};
      this.setShippingField('company', shipping.company || ctx?.company?.name || '');
      this.setShippingField('phone', shipping.phone || ctx?.phone || '');
      this.setShippingField('address1', shipping.address1 || '');
      this.setShippingField('address2', shipping.address2 || '');
      this.setShippingField('zip', shipping.zip || '');
      this.setShippingField('city', shipping.city || '');
      this.setShippingField('province', shipping.province || shipping.province_code || '');
      this.setShippingField('country', shipping.country || shipping.country_code || '');
      const editable = Boolean(ctx?.editable_shipping_address);
      this.querySelectorAll('[data-b2b-shipping-field]').forEach((input) => { input.disabled = !editable; });
      this.updateLocalTotals();
    }

    shippingPayload() {
      const values = {};
      this.querySelectorAll('[data-b2b-shipping-field]').forEach((input) => {
        values[input.dataset.b2bShippingField] = input.value.trim();
      });
      return values;
    }

    orderPayload() {
      return {
        cart_token: this.config?.cart_token || null,
        request_id: this.requestId || null,
        company_location_id: this.config?.company_location_id || this.dataset.companyLocationId,
        items: Array.isArray(this.config?.items)
          ? this.config.items.map((item) => ({ variant_id: String(item.variant_id || ''), quantity: Number(item.quantity) || 0 })).filter((item) => item.variant_id && item.quantity > 0)
          : [],
        payment_mode: this.currentPaymentMode(),
        po_number: this.querySelector('[data-b2b-po-number]')?.value.trim() || '',
        note: this.querySelector('[data-b2b-order-note]')?.value.trim() || '',
        shipping_address: this.shippingPayload(),
      };
    }

    applyPreview(preview) {
      if (!preview) return;
      this.preview = preview;

      // El servidor B2B devuelve la dirección validada por Shopify junto al
      // cálculo. La rehidratamos aquí para que ninguna actualización del
      // resumen económico pueda dejar vacíos los campos de envío.
      const previewShipping = preview?.shipping_address || {};
      if (Object.keys(previewShipping).length) {
        const editable = Boolean(this.context?.editable_shipping_address);
        const syncField = (name, value) => {
          const input = this.querySelector(`[data-b2b-shipping-field="${name}"]`);
          if (!input || value == null) return;
          // Si Shopify permite editar la dirección, respetamos cualquier cambio
          // que el comprador ya haya escrito y solo completamos campos vacíos.
          if (!editable || !input.value.trim()) input.value = String(value);
        };
        syncField('company', previewShipping.company || this.context?.company?.name || '');
        syncField('phone', previewShipping.phone || this.context?.phone || '');
        syncField('address1', previewShipping.address1 || '');
        syncField('address2', previewShipping.address2 || '');
        syncField('zip', previewShipping.zip || '');
        syncField('city', previewShipping.city || '');
        syncField('province', previewShipping.province || previewShipping.province_code || '');
        syncField('country', previewShipping.country || previewShipping.country_code || '');
      }

      // La dirección registrada de facturación también se pinta con los datos
      // reales devueltos por el servidor, en vez de depender solo del Liquid
      // inicial de Shopify.
      const previewBilling = preview?.billing_address || {};
      const billing = this.querySelector('[data-b2b-billing-address]');
      if (billing && Object.keys(previewBilling).length) {
        const lines = [
          previewBilling.address1,
          previewBilling.address2,
          [previewBilling.zip, previewBilling.city].filter(Boolean).join(' '),
          [previewBilling.province, previewBilling.country].filter(Boolean).join(', '),
        ].filter(Boolean);
        billing.replaceChildren();
        (lines.length ? lines : ['—']).forEach((line, index) => {
          if (index) billing.appendChild(document.createElement('br'));
          billing.appendChild(document.createTextNode(String(line)));
        });
      }

      const cents = (key) => Math.round(Number(preview[key] || 0) * 100);
      if (preview.pairs != null) {
        this.pairs = Number(preview.pairs) || this.pairs;
        const pairs = this.querySelector('[data-b2b-total-pairs]');
        if (pairs) pairs.textContent = String(preview.pairs);
      }
      const habitual = cents('habitual_discount');
      const original = cents('original_merchandise');
      const merchandise = cents('merchandise');
      const habitualRow = this.querySelector('[data-b2b-habitual-row]');
      const originalRow = this.querySelector('[data-b2b-original-row]');
      const habitualLabel = this.querySelector('[data-b2b-habitual-label]');
      this.habitualCents = habitual;
      this.originalCents = original;
      this.merchandiseCents = merchandise;
      this.habitualPercent = Number(preview?.habitual_discount_percent || 0) || 0;
      if (habitualRow) habitualRow.hidden = habitual <= 0 || this.habitualPercent <= 0;
      if (originalRow) originalRow.hidden = habitual <= 0 || this.habitualPercent <= 0;
      if (habitualLabel) {
        const rounded = Math.round(this.habitualPercent * 10) / 10;
        const percentText = Number.isInteger(rounded) ? String(rounded) : String(rounded).replace('.', ',');
        habitualLabel.textContent = habitual > 0 ? `Descuento habitual (${percentText} %)` : 'Descuento habitual';
      }
      this.setMoney('[data-b2b-original-merchandise]', original);
      this.setMoney('[data-b2b-habitual-discount]', habitual, '−');
      this.setMoney('[data-b2b-merchandise]', merchandise);
      const early = cents('early_payment_discount');
      const earlyRow = this.querySelector('[data-b2b-early-row]');
      if (earlyRow) earlyRow.hidden = early <= 0;
      this.setMoney('[data-b2b-early-discount]', early, '−');
      const shipping = this.querySelector('[data-b2b-shipping-amount]');
      if (shipping) shipping.textContent = cents('shipping') === 0 ? 'GRATIS' : this.money(cents('shipping'));
      this.setMoney('[data-b2b-tax-base]', cents('tax_base'));
      this.setMoney('[data-b2b-iva]', cents('iva'));
      this.hasRE = Number(preview.re || 0) > 0 || Boolean(this.context?.recargo_equivalencia);
      const reRow = this.querySelector('[data-b2b-re-row]');
      if (reRow) reRow.hidden = !this.hasRE;
      this.setMoney('[data-b2b-re]', cents('re'));
      this.setMoney('[data-b2b-grand-total]', cents('total'));
      this.calculation = {
        earlyCents: early,
        shippingCents: cents('shipping'),
        baseCents: cents('tax_base'),
        ivaCents: cents('iva'),
        reCents: cents('re'),
        totalCents: cents('total'),
      };
      const note = this.querySelector('.clayan-b2b-final-order__calculation-note');
      if (note) note.textContent = 'Importe validado por el servicio B2B de Clayan con precios, portes e impuestos que se usarán al crear el pedido.';
    }

    async refreshPreview() {
      if (!this.serviceReady) return;
      const preview = await this.fetchJSON(this.proxyUrl('/preview'), {
        method: 'POST',
        body: JSON.stringify(this.orderPayload()),
      });
      this.applyPreview(preview);
    }

    async loadContextAndPreview() {
      if (!this.config?.customer_id || !this.config?.company_location_id) {
        this.setServiceStatus('Falta contexto B2B', 'error');
        this.setMessage('No se ha podido identificar correctamente el cliente o la empresa B2B.', true);
        return;
      }
      try {
        this.setServiceStatus('Conectando servicio B2B…', 'loading');
        const ctx = await this.fetchJSON(this.proxyUrl('/context'));
        this.serviceReady = true;
        this.applyContext(ctx);
        await this.refreshPreview();
        this.setServiceStatus('Pedido listo para enviar', 'ready');
      } catch (error) {
        this.serviceReady = false;
        this.setServiceStatus('Servidor B2B pendiente', 'error');
        const note = this.querySelector('.clayan-b2b-final-order__calculation-note');
        if (note) note.textContent = 'La pantalla está preparada. Para crear el pedido real y actualizar stock falta conectar el servidor B2B de Clayan.';
      }
    }

    clearPersistentBackupAfterOrder() {
      try {
        const customerId = document.body?.dataset?.clayanB2bCustomerId || this.config?.customer_id || '';
        if (customerId) localStorage.removeItem(`clayan_b2b_cart_v1:${customerId}`);
      } catch (error) {}
    }

    async submitOrder() {
      if (!this.submitButton || this.submitButton.disabled) return;
      if (!this.confirm?.checked) {
        this.setMessage('Marca la casilla de revisión antes de enviar el pedido.', true);
        this.confirm?.focus();
        return;
      }
      if (!this.serviceReady) {
        this.setMessage('El servidor B2B todavía no está conectado. El pedido no se ha enviado y el carrito se conserva.', true);
        return;
      }

      const originalText = this.submitButton.textContent;
      this.submitButton.disabled = true;
      this.submitButton.setAttribute('aria-busy', 'true');
      this.submitButton.textContent = 'ENVIANDO PEDIDO…';
      this.setMessage('Registrando el pedido y reservando stock…');
      this.setServiceStatus('Creando pedido…', 'loading');

      try {
        await this.refreshPreview();
        this.saveOrderSnapshot();
        const finalPayload = this.orderPayload();
        // Enviamos al servidor la misma instantánea visual que usa el botón
        // Imprimir/Descargar PDF, para adjuntar ese mismo documento al correo.
        finalPayload.print_snapshot = this.orderSnapshot();
        const result = await this.fetchJSON(this.proxyUrl('/order'), {
          method: 'POST',
          body: JSON.stringify(finalPayload),
        });

        const root = window.Shopify?.routes?.root || '/';
        await fetch(`${root}cart/clear.js`, {
          method: 'POST',
          credentials: 'same-origin',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify({}),
        });
        this.clearPersistentBackupAfterOrder();
        try { sessionStorage.removeItem('clayanB2BOrderRequestId'); } catch (error) {}

        this.querySelector('.clayan-b2b-final-order__grid')?.setAttribute('hidden', '');
        this.querySelector('.clayan-b2b-final-order__submit-area')?.setAttribute('hidden', '');
        this.querySelector('.clayan-b2b-final-order__header')?.setAttribute('hidden', '');
        if (this.success) this.success.hidden = false;
        const copy = this.querySelector('[data-b2b-success-copy]');
        if (copy) {
          const orderName = result.order_name || 'El pedido';
          const factoryFailed = result.factory_email_sent === false;
          const customerFailed = result.customer_email_sent === false;
          const pdfFailed = result.pdf_generated === false;
          if (pdfFailed) {
            copy.textContent = `${orderName} se ha registrado y el stock ha quedado reservado, pero no se ha podido generar el PDF del pedido.`;
          } else if (factoryFailed && customerFailed) {
            copy.textContent = `${orderName} se ha registrado y el stock ha quedado reservado, pero no se ha podido enviar el PDF ni a fábrica ni al cliente.`;
          } else if (factoryFailed) {
            copy.textContent = `${orderName} se ha registrado y el PDF ha sido enviado al cliente, pero no se ha podido enviar a fábrica.`;
          } else if (customerFailed) {
            copy.textContent = `${orderName} se ha registrado y el PDF ha sido enviado a fábrica, pero no se ha podido enviar al cliente.`;
          } else {
            copy.textContent = `${orderName} se ha registrado, el stock ha quedado reservado y el PDF de confirmación ha sido enviado a fábrica y al cliente.`;
          }
        }
        updateShippingUI(0);
      } catch (error) {
        this.setMessage(error.message || 'No se pudo crear el pedido. El carrito se conserva.', true);
        this.setServiceStatus('Pedido no enviado', 'error');
        this.submitButton.disabled = false;
        this.submitButton.textContent = originalText;
      } finally {
        this.submitButton.removeAttribute('aria-busy');
      }
    }
  }

  if (!customElements.get('clayan-b2b-final-order')) {
    customElements.define('clayan-b2b-final-order', ClayanB2BFinalOrder);
  }

  const escapeHTML = (value) => String(value ?? '')
    .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;').replace(/'/g, '&#039;');

  const renderVisualOrderForPrint = () => {
    const sheet = document.querySelector('[data-b2b-print-sheet]');
    if (!sheet) return false;
    // Mover el documento de impresión al BODY evita que los contenedores
    // del tema/cart conserven altura y creen páginas en blanco al imprimir.
    if (sheet.parentElement !== document.body) document.body.appendChild(sheet);
    let snapshot = null;
    try { snapshot = JSON.parse(sessionStorage.getItem('clayan_b2b_last_order_visual_v1') || 'null'); } catch (error) {}
    if (!snapshot) {
      sheet.innerHTML = '<p>No se ha podido recuperar el detalle del pedido para imprimir.</p>';
      return false;
    }
    const sizes = Array.from({ length: 15 }, (_, i) => 35 + i);
    const rows = Array.isArray(snapshot.rows) ? snapshot.rows : [];
    const rowHTML = rows.map((row) => {
      const cells = sizes.map((size) => `<td>${Number(row?.sizes?.[size] || 0) || ''}</td>`).join('');
      return `<tr><td>${escapeHTML(row.model || '—')}</td><td>${escapeHTML(row.description || '—')}</td>${cells}<td>${escapeHTML(row.total || 0)}</td></tr>`;
    }).join('');
    const totals = snapshot.totals || {};
    sheet.innerHTML = `
      <div class="clayan-b2b-print-doc">
        <header class="clayan-b2b-print-doc__top">
          <div><div class="clayan-b2b-print-doc__brand">CLAYAN SHOES, S.L.</div><div class="clayan-b2b-print-doc__title">PEDIDO B2B</div></div>
          <div class="clayan-b2b-print-doc__meta">
            <div><span class="clayan-b2b-print-doc__label">Fecha</span><span class="clayan-b2b-print-doc__value">${escapeHTML(snapshot.dateText || '')}</span></div>
            <div><span class="clayan-b2b-print-doc__label">Total pares</span><span class="clayan-b2b-print-doc__value">${escapeHTML(snapshot.totalPairs || 0)}</span></div>
            <div><span class="clayan-b2b-print-doc__label">Referencia</span><span class="clayan-b2b-print-doc__value">${escapeHTML(snapshot.reference || '—')}</span></div>
            <div><span class="clayan-b2b-print-doc__label">Forma de pago</span><span class="clayan-b2b-print-doc__value">${escapeHTML(snapshot.payment || '—')}</span></div>
          </div>
        </header>
        <section class="clayan-b2b-print-doc__info">
          <div><span class="clayan-b2b-print-doc__label">Empresa</span><span class="clayan-b2b-print-doc__value">${escapeHTML(snapshot.company || '—')}</span></div>
          <div><span class="clayan-b2b-print-doc__label">Contacto</span><span class="clayan-b2b-print-doc__value">${escapeHTML(snapshot.contact || '—')}<br>${escapeHTML(snapshot.email || '—')}</span></div>
          <div><span class="clayan-b2b-print-doc__label">NIF / CIF</span><span class="clayan-b2b-print-doc__value">${escapeHTML(snapshot.taxId || '—')}</span></div>
          <div><span class="clayan-b2b-print-doc__label">Agente comercial</span><span class="clayan-b2b-print-doc__value">${escapeHTML(snapshot.agent || '—')}</span></div>
          <div><span class="clayan-b2b-print-doc__label">Teléfono</span><span class="clayan-b2b-print-doc__value">${escapeHTML(snapshot.phone || '—')}</span></div>
        </section>
        <div><span class="clayan-b2b-print-doc__label">Dirección de envío</span><span class="clayan-b2b-print-doc__value">${escapeHTML(snapshot.shipping || '—').replace(/\n/g, ' · ')}</span></div>
        <table class="clayan-b2b-print-doc__table">
          <thead><tr><th>Modelo</th><th>Descripción</th>${sizes.map((s) => `<th>${s}</th>`).join('')}<th>Total</th></tr></thead>
          <tbody>${rowHTML || '<tr><td colspan="18">Sin líneas de pedido</td></tr>'}</tbody>
        </table>
        <section class="clayan-b2b-print-doc__bottom">
          <div class="clayan-b2b-print-doc__notes">
            <span class="clayan-b2b-print-doc__label">Observaciones</span>
            <div>${escapeHTML(snapshot.note || '—')}</div>
          </div>
          <table class="clayan-b2b-print-doc__totals">
            <tbody>
              ${totals.hasHabitual ? `<tr><td>Subtotal antes de descuentos</td><td>${escapeHTML(totals.original || '—')}</td></tr><tr><td>${escapeHTML(totals.habitualLabel || 'Descuento habitual')}</td><td>${escapeHTML(totals.habitual || '—')}</td></tr>` : ''}
              <tr><td>Mercancía B2B sin IVA</td><td>${escapeHTML(totals.merchandise || '—')}</td></tr>
              <tr><td>Pronto pago −3 %</td><td>${escapeHTML(totals.early || '—')}</td></tr>
              <tr><td>Portes</td><td>${escapeHTML(totals.shipping || '—')}</td></tr>
              <tr><td>Base imponible</td><td>${escapeHTML(totals.base || '—')}</td></tr>
              <tr><td>IVA 21 %</td><td>${escapeHTML(totals.iva || '—')}</td></tr>
              <tr><td>R.E. 5,2 %</td><td>${escapeHTML(totals.re || '—')}</td></tr>
              <tr><td>TOTAL PEDIDO</td><td>${escapeHTML(totals.total || '—')}</td></tr>
            </tbody>
          </table>
        </section>
        <footer class="clayan-b2b-print-doc__footer">Pedido recibido desde el portal profesional de Clayan Shoes.</footer>
      </div>`;
    return true;
  };

  document.addEventListener('DOMContentLoaded', () => {
    if (document.querySelector('[data-b2b-print-sheet]')) renderVisualOrderForPrint();
  });

  document.addEventListener('click', (event) => {
    const button = event.target.closest('[data-b2b-print-order]');
    if (!button) return;
    event.preventDefault();
    const order = document.querySelector('clayan-b2b-final-order');
    if (order && typeof order.saveOrderSnapshot === 'function') order.saveOrderSnapshot();
    if (renderVisualOrderForPrint()) window.print();
  });

  /* ========================================================================
     V10.1 — CARRITO B2B PERSISTENTE POR CLIENTE
     Guarda variantes/cantidades en localStorage para que cerrar sesión no
     haga perder un pedido en curso. Se restaura al volver a entrar con el
     mismo cliente en el mismo navegador/dispositivo.
     ======================================================================== */
  const persistentCartCustomerId = document.body?.dataset?.clayanB2bCustomerId || '';
  const persistentCartKey = persistentCartCustomerId ? `clayan_b2b_cart_v1:${persistentCartCustomerId}` : '';
  let persistentCartRestoring = false;

  const normalizePersistentItems = (cartOrItems) => {
    const items = Array.isArray(cartOrItems) ? cartOrItems : (Array.isArray(cartOrItems?.items) ? cartOrItems.items : []);
    return items
      .map((item) => ({
        id: Number(item.id || item.variant_id || 0),
        quantity: Math.max(0, Number(item.quantity || 0)),
      }))
      .filter((item) => Number.isFinite(item.id) && item.id > 0 && item.quantity > 0);
  };

  const readPersistentCart = () => {
    if (!persistentCartKey) return null;
    try {
      const parsed = JSON.parse(localStorage.getItem(persistentCartKey) || 'null');
      if (!parsed || !Array.isArray(parsed.items)) return null;
      return parsed;
    } catch (error) {
      return null;
    }
  };

  const writePersistentCart = (cartOrItems) => {
    if (!persistentCartKey || persistentCartRestoring) return;
    const items = normalizePersistentItems(cartOrItems);
    try {
      if (!items.length) {
        localStorage.removeItem(persistentCartKey);
        return;
      }
      localStorage.setItem(persistentCartKey, JSON.stringify({
        customer_id: persistentCartCustomerId,
        saved_at: Date.now(),
        items,
      }));
    } catch (error) {
      // Si el navegador bloquea localStorage, el carrito nativo sigue funcionando.
    }
  };

  const fetchCurrentCart = async () => {
    const root = window.Shopify?.routes?.root || '/';
    const response = await fetch(`${root}cart.js`, {
      headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      credentials: 'same-origin',
    });
    if (!response.ok) throw new Error('No se pudo leer el carrito.');
    return response.json();
  };

  const restorePersistentCart = async () => {
    if (!persistentCartKey || persistentCartRestoring) return;
    const saved = readPersistentCart();
    if (!saved?.items?.length) {
      try {
        const current = await fetchCurrentCart();
        if ((current?.item_count || 0) > 0) writePersistentCart(current);
      } catch (error) {}
      return;
    }

    try {
      const current = await fetchCurrentCart();
      if ((current?.item_count || 0) > 0) {
        writePersistentCart(current);
        return;
      }

      persistentCartRestoring = true;
      const root = window.Shopify?.routes?.root || '/';
      const savedItems = normalizePersistentItems(saved.items);
      if (!savedItems.length) return;

      let restoredAny = false;
      try {
        const response = await fetch(`${root}cart/add.js`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json',
            'X-Requested-With': 'XMLHttpRequest',
          },
          credentials: 'same-origin',
          body: JSON.stringify({ items: savedItems }),
        });
        restoredAny = response.ok;
      } catch (error) {}

      // Si una variante ya no se puede vender, intentamos restaurar el resto una a una.
      if (!restoredAny) {
        for (const item of savedItems) {
          try {
            const response = await fetch(`${root}cart/add.js`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'Accept': 'application/json',
                'X-Requested-With': 'XMLHttpRequest',
              },
              credentials: 'same-origin',
              body: JSON.stringify({ items: [item] }),
            });
            if (response.ok) restoredAny = true;
          } catch (error) {}
        }
      }

      if (restoredAny) {
        const restoredCart = await fetchCurrentCart();
        try {
          localStorage.setItem(persistentCartKey, JSON.stringify({
            customer_id: persistentCartCustomerId,
            saved_at: Date.now(),
            items: normalizePersistentItems(restoredCart),
          }));
        } catch (error) {}
        updateShippingUI(restoredCart.item_count || 0);

        // Recargar garantiza que el drawer, el resumen y todas las secciones
        // de Dawn reflejen inmediatamente el carrito restaurado.
        window.location.reload();
      }
    } catch (error) {
      // Restauración no crítica: nunca debe bloquear la compra.
    } finally {
      persistentCartRestoring = false;
    }
  };

  const syncPersistentCart = async () => {
    if (!persistentCartKey || persistentCartRestoring) return;
    try {
      const cart = await fetchCurrentCart();
      writePersistentCart(cart);
    } catch (error) {}
  };



  // V12.6 — vaciar carrito B2B completo (carrito actual + persistencia local).
  document.addEventListener('click', async (event) => {
    const button = event.target.closest('[data-b2b-clear-cart]');
    if (!button) return;
    event.preventDefault();

    if (!window.confirm('¿Quieres vaciar todo el carrito?')) return;

    const originalText = button.textContent;
    button.disabled = true;
    button.textContent = 'Vaciando…';

    try {
      const root = window.Shopify?.routes?.root || '/';
      const response = await fetch(`${root}cart/clear.js`, {
        method: 'POST',
        headers: {
          'Accept': 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
        },
        credentials: 'same-origin',
      });
      if (!response.ok) throw new Error('No se pudo vaciar el carrito.');

      if (persistentCartKey) {
        try { localStorage.removeItem(persistentCartKey); } catch (error) {}
      }
      try { sessionStorage.removeItem('clayanB2BOrderRequestId'); } catch (error) {}

      window.location.reload();
    } catch (error) {
      button.disabled = false;
      button.textContent = originalText;
      window.alert('No se ha podido vaciar el carrito. Inténtalo de nuevo.');
    }
  });

  if (persistentCartKey) {
    document.addEventListener('DOMContentLoaded', () => {
      restorePersistentCart();
    });

    // Guardamos después de cambios de carrito emitidos por Dawn/tema.
    document.addEventListener('cart:updated', () => {
      window.setTimeout(syncPersistentCart, 80);
    });

    if (typeof window.subscribe === 'function' && window.PUB_SUB_EVENTS?.cartUpdate) {
      window.subscribe(window.PUB_SUB_EVENTS.cartUpdate, () => {
        window.setTimeout(syncPersistentCart, 80);
      });
    } else if (typeof subscribe === 'function' && window.PUB_SUB_EVENTS?.cartUpdate) {
      subscribe(window.PUB_SUB_EVENTS.cartUpdate, () => {
        window.setTimeout(syncPersistentCart, 80);
      });
    }

    // Cubre actualizaciones nativas de cantidad/eliminación aunque el tema no
    // emita el evento anterior en alguna vista concreta.
    document.addEventListener('change', (event) => {
      if (event.target?.matches?.('input[name="updates[]"], [data-quantity-variant-id]')) {
        window.setTimeout(syncPersistentCart, 650);
      }
    });
  }

})();
